import { useState } from 'react';
import type { FormEvent } from 'react';
import { Check } from 'lucide-react';

import { Checkbox } from '~shared/shadcn/ui/checkbox';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import type {
  StudioFormFieldVisibility,
  StudioFormTheme,
} from '~features/form-submissions/api/types';
import { getErrorMessage } from '~shared/utils/error-handler';
import { isValidFormPersonName } from '../lib/form-validation';

const DEFAULT_PRIMARY = '#6d28d9';

export interface StudioFormValues {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  childFirstName: string;
  childAge: string;
  sessionInterest: string;
  consent: boolean;
  website: string;
}

const EMPTY_VALUES: StudioFormValues = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  childFirstName: '',
  childAge: '',
  sessionInterest: '',
  consent: false,
  website: '',
};

interface StudioFormCardProps {
  theme: StudioFormTheme;
  fieldVisibility?: StudioFormFieldVisibility;
  interestOptions: Array<string>;
  studioName?: string;
  onSubmit?: (values: StudioFormValues) => Promise<void>;
  interactive?: boolean;
}

export function StudioFormCard({
  theme,
  fieldVisibility,
  interestOptions,
  studioName,
  onSubmit,
  interactive = true,
}: StudioFormCardProps) {
  const [values, setValues] = useState<StudioFormValues>(EMPTY_VALUES);
  const [status, setStatus] = useState<
    'idle' | 'submitting' | 'success' | 'error'
  >('idle');
  const [error, setError] = useState<string>('');

  const primary = theme.primaryColor ?? DEFAULT_PRIMARY;
  const title = theme.formTitle ?? 'View Class Availability';
  const submitLabel = theme.submitLabel ?? 'Submit';
  const consentText =
    theme.consentText ??
    `I agree to be contacted by ${studioName ?? 'the studio'} about classes and enrollment via phone, text, or email.`;

  const showChild = fieldVisibility?.childFirstName ?? true;
  const showChildAge = fieldVisibility?.childAge ?? false;
  const showInterest =
    (fieldVisibility?.interest ?? true) && interestOptions.length > 0;
  const interestAsDropdown = fieldVisibility?.interestAsDropdown ?? false;

  const set = <K extends keyof StudioFormValues>(
    key: K,
    value: StudioFormValues[K],
  ) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  // Same person-name rule the API enforces (ENR-1474); warn before submit.
  const nameWarnings = {
    firstName:
      values.firstName !== '' && !isValidFormPersonName(values.firstName),
    lastName:
      values.lastName !== '' && !isValidFormPersonName(values.lastName),
    childFirstName:
      values.childFirstName !== '' &&
      !isValidFormPersonName(values.childFirstName),
  };
  const hasNameWarning =
    nameWarnings.firstName ||
    nameWarnings.lastName ||
    nameWarnings.childFirstName;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!interactive || !onSubmit || hasNameWarning) return;
    setStatus('submitting');
    setError('');
    void onSubmit(values)
      .then(() => { setStatus('success'); })
      .catch((submitError: unknown) => {
        setStatus('error');
        setError(
          getErrorMessage(
            submitError,
            'Something went wrong. Please try again.',
          ),
        );
      });
  };

  if (status === 'success') {
    return (
      <div className='w-full max-w-md rounded-2xl border bg-white p-8 text-center shadow-sm'>
        <div
          className='mx-auto mb-4 flex size-12 items-center justify-center rounded-full'
          style={{ backgroundColor: `${primary}1a` }}
        >
          <Check className='size-6' style={{ color: primary }} />
        </div>
        <h2 className='text-xl font-semibold text-gray-900'>Thank you!</h2>
        <p className='mt-2 text-sm text-gray-600'>
          We received your request and will be in touch soon.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className='w-full max-w-md overflow-hidden break-words rounded-2xl border bg-white p-6 shadow-sm'
    >
      {theme.logoUrl ? (
        <img
          src={theme.logoUrl}
          alt={studioName ?? 'Studio logo'}
          className='mx-auto mb-4 h-12 w-auto object-contain'
        />
      ) : null}
      <h2 className='break-words text-center text-xl font-semibold text-gray-900'>
        {title}
      </h2>
      {theme.introText ? (
        <p className='mt-1 break-words text-center text-sm text-gray-600'>
          {theme.introText}
        </p>
      ) : null}

      <div className='mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2'>
        <div className='space-y-1.5'>
          <Label htmlFor='enr-form-first-name'>First name</Label>
          <Input
            id='enr-form-first-name'
            required
            maxLength={50}
            disabled={!interactive}
            value={values.firstName}
            onChange={(e) => { set('firstName', e.target.value); }}
          />
          {nameWarnings.firstName ? (
            <p className='text-xs text-red-600' role='alert'>
              Letters, spaces, hyphens, apostrophes and periods only (max 50).
            </p>
          ) : null}
        </div>
        <div className='space-y-1.5'>
          <Label htmlFor='enr-form-last-name'>Last name</Label>
          <Input
            id='enr-form-last-name'
            required
            maxLength={50}
            disabled={!interactive}
            value={values.lastName}
            onChange={(e) => { set('lastName', e.target.value); }}
          />
          {nameWarnings.lastName ? (
            <p className='text-xs text-red-600' role='alert'>
              Letters, spaces, hyphens, apostrophes and periods only (max 50).
            </p>
          ) : null}
        </div>
        <div className='space-y-1.5'>
          <Label htmlFor='enr-form-phone'>Phone</Label>
          <Input
            id='enr-form-phone'
            type='tel'
            required
            disabled={!interactive}
            value={values.phone}
            onChange={(e) => { set('phone', e.target.value); }}
          />
        </div>
        <div className='space-y-1.5'>
          <Label htmlFor='enr-form-email'>Email</Label>
          <Input
            id='enr-form-email'
            type='email'
            required
            disabled={!interactive}
            value={values.email}
            onChange={(e) => { set('email', e.target.value); }}
          />
        </div>
        {showChild ? (
          <div className='space-y-1.5'>
            <Label htmlFor='enr-form-child-first-name'>Student&apos;s first name</Label>
            <Input
              id='enr-form-child-first-name'
              maxLength={50}
              disabled={!interactive}
              value={values.childFirstName}
              onChange={(e) => { set('childFirstName', e.target.value); }}
            />
            {nameWarnings.childFirstName ? (
              <p className='text-xs text-red-600' role='alert'>
                Letters, spaces, hyphens, apostrophes and periods only (max 50).
              </p>
            ) : null}
          </div>
        ) : null}
        {showChildAge ? (
          <div className='space-y-1.5'>
            <Label htmlFor='enr-form-child-age'>Student&apos;s age</Label>
            <Input
              id='enr-form-child-age'
              inputMode='decimal'
              disabled={!interactive}
              value={values.childAge}
              onChange={(e) => { set('childAge', e.target.value); }}
            />
          </div>
        ) : null}
      </div>

      {showInterest ? (
        <div className='mt-4 space-y-1.5'>
          <Label>I&apos;m interested in</Label>
          {interestAsDropdown ? (
            <Select
              disabled={!interactive}
              value={values.sessionInterest}
              onValueChange={(value) => { set('sessionInterest', value); }}
            >
              <SelectTrigger className='w-full'>
                <SelectValue placeholder='Select an option' />
              </SelectTrigger>
              <SelectContent>
                {interestOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className='flex flex-wrap gap-2'>
              {interestOptions.map((option) => {
                const selected = values.sessionInterest === option;
                return (
                  <button
                    key={option}
                    type='button'
                    disabled={!interactive}
                    onClick={() =>
                      { set('sessionInterest', selected ? '' : option); }
                    }
                    className='max-w-full break-words rounded-full border px-3 py-1.5 text-left text-sm transition-colors'
                    style={
                      selected
                        ? {
                            backgroundColor: primary,
                            borderColor: primary,
                            color: '#ffffff',
                          }
                        : { borderColor: '#d1d5db', color: '#374151' }
                    }
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : null}

      {/* Honeypot - hidden from humans, filled by bots */}
      <div
        aria-hidden='true'
        style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
      >
        <label htmlFor='enr-form-website'>Website</label>
        <input
          id='enr-form-website'
          name='website'
          type='text'
          tabIndex={-1}
          autoComplete='off'
          value={values.website}
          onChange={(e) => { set('website', e.target.value); }}
        />
      </div>

      <div className='mt-4 flex items-start gap-2'>
        <Checkbox
          id='enr-form-consent'
          required
          disabled={!interactive}
          checked={values.consent}
          onCheckedChange={(checked) => { set('consent', checked === true); }}
        />
        <Label
          htmlFor='enr-form-consent'
          className='break-words text-xs font-normal leading-snug text-gray-600'
        >
          {consentText}
        </Label>
      </div>

      {status === 'error' ? (
        <p className='mt-3 text-sm text-red-600' role='alert'>
          {error}
        </p>
      ) : null}

      <button
        type='submit'
        disabled={!interactive || status === 'submitting' || hasNameWarning}
        className='mt-5 w-full rounded-full py-2.5 text-sm font-semibold text-white transition-opacity disabled:opacity-60'
        style={{ backgroundColor: primary }}
      >
        {status === 'submitting' ? 'Submitting...' : submitLabel}
      </button>
    </form>
  );
}
